import { ArrowLeft, DotsThree, Lightning, Play, Warning } from '@phosphor-icons/react';
import {
  getGetFlowQueryKey,
  getListFlowsQueryKey,
  useDeleteFlow,
  useUpdateFlow,
} from '@reelplicate/api-client/flows';
import type { FlowDto, FlowStatus } from '@reelplicate/api-client/model';
import { getNodeDefinition } from '@reelplicate/flows';
import {
  Badge,
  Button,
  cn,
  ConfirmDialog,
  IconButton,
  Menu,
  MenuContent,
  MenuDivider,
  MenuItem,
  MenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TextInput,
  toast,
  Tooltip,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { formatWhen } from '@/lib/format-when';

import { STATUS_BADGE } from '../flow-status';
import { FlowViewSwitch } from '../flow-view-switch';
import { useEditorStore } from './editor-store';
import { RunBadge } from './run/run-badge';
import { isOpen, useRun } from './run/run-state';
import { useAnalysis } from './use-analysis';
import type { SaveStatus } from './use-autosave';

type Props = {
  workspaceId: string;
  workspaceName: string;
  flow: FlowDto;
  save: { status: SaveStatus; savedAt: Date; flush: () => Promise<boolean> };
};

/**
 * The editor's top bar (Figma 158:861): back, the flow's name and status, where
 * saving is, the cost estimate, and the flow's actions. Test run and Run flow
 * arrive with the run engine, so they're shown but not yet pressable.
 */
export function EditorTopBar({ workspaceId, workspaceName, flow, save }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const store = useEditorStore();
  const { issues, estimate } = useAnalysis();
  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');
  // A run asked for while there are warnings, waiting for "Run anyway".
  const [confirming, setConfirming] = useState<'TEST' | 'FULL' | null>(null);
  const update = useUpdateFlow();
  const remove = useDeleteFlow();
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const run = useRun();
  const runOpen = isOpen(run.run);
  const startRun = (mode: 'TEST' | 'FULL') => {
    if (warnings.length > 0) setConfirming(mode);
    else void run.start(mode);
  };
  const nodeLabel = (nodeId?: string) => {
    const node = nodeId && store.getState().graph.nodes.find((n) => n.id === nodeId);
    return node
      ? (node.label ?? getNodeDefinition(node.type, node.typeVersion)?.label)
      : undefined;
  };

  const refresh = async (next?: FlowDto) => {
    if (next) queryClient.setQueryData(getGetFlowQueryKey(workspaceId, flow.id), next);
    await queryClient.invalidateQueries({ queryKey: getListFlowsQueryKey(workspaceId) });
  };

  const rename = async (name: string) => {
    setRenaming(false);
    const trimmed = name.trim();
    if (!trimmed || trimmed === flow.name) return;
    try {
      await refresh(
        await update.mutateAsync({
          id: workspaceId,
          flowId: flow.id,
          data: { name: trimmed },
        }),
      );
    } catch {
      return; // The failure is already shown as a toast.
    }
  };

  const setStatus = async (status: FlowStatus) => {
    if (!(await save.flush())) {
      toast.error('Save your changes first', {
        description: 'The flow couldn’t be saved just now.',
      });
      return;
    }
    try {
      const next = await update.mutateAsync({
        id: workspaceId,
        flowId: flow.id,
        data: { status },
      });
      await refresh(next);
      if (status === 'ACTIVE') {
        const first = next.nextRunAt ? ` · next run ${formatWhen(next.nextRunAt)}` : '';
        toast.success(
          (flow.status === 'ACTIVE'
            ? `Active version is now v${next.activeVersion?.number}`
            : 'Flow activated') + first,
        );
      } else toast(status === 'PAUSED' ? 'Flow paused' : 'Flow moved back to draft');
    } catch {
      return; // The failure (e.g. the draft's errors) is already shown as a toast.
    }
  };

  const deleteFlow = async () => {
    try {
      await remove.mutateAsync({ id: workspaceId, flowId: flow.id });
    } catch {
      return;
    }
    await refresh();
    toast.success(`${flow.name} deleted`);
    void navigate('/flows');
  };

  const badge = STATUS_BADGE[flow.status];
  return (
    <header className="bg-bg-surface border-border-subtle flex h-15 shrink-0 items-center gap-2 border-b pr-4 pl-3">
      <Tooltip content="All flows">
        <IconButton
          icon={ArrowLeft}
          aria-label="All flows"
          onClick={() => void navigate('/flows')}
        />
      </Tooltip>
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 items-center gap-2">
          {renaming ? (
            <TextInput
              size="sm"
              aria-label="Flow name"
              defaultValue={flow.name}
              autoFocus
              maxLength={80}
              className="w-80"
              onBlur={(event) => void rename(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void rename(event.currentTarget.value);
                if (event.key === 'Escape') setRenaming(false);
              }}
            />
          ) : (
            <h1 className="text-heading-h4 text-text-primary min-w-0">
              <button
                type="button"
                className="focus-visible:shadow-focus-ring block max-w-120 truncate rounded-sm text-left outline-none"
                title="Rename"
                onClick={() => setRenaming(true)}
              >
                {flow.name}
              </button>
            </h1>
          )}
          <Badge tone={badge.tone} dot={flow.status !== 'DRAFT'}>
            {badge.label}
            {flow.status === 'ACTIVE' && flow.activeVersion
              ? ` · v${flow.activeVersion.number}`
              : ''}
          </Badge>
          <RunBadge run={run.run} />
        </div>
        <p className="text-caption text-text-secondary truncate" aria-live="polite">
          {workspaceName} · <SaveLabel status={save.status} savedAt={save.savedAt} />
          {flow.nextRunAt && ` · Next run ${formatWhen(flow.nextRunAt)}`}
        </p>
      </div>

      <div className="flex-1" />
      <FlowViewSwitch flowId={flow.id} view="canvas" />
      <div className="flex-1" />

      {errors.length + warnings.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={Warning}
              className={
                errors.length ? 'text-status-danger-fg' : 'text-status-warning-fg'
              }
            >
              {[
                errors.length &&
                  `${errors.length} ${errors.length === 1 ? 'issue' : 'issues'}`,
                warnings.length &&
                  `${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" aria-label="Issues" className="w-80 p-2">
            <ul className="flex flex-col">
              {[...errors, ...warnings].map((issue, i) => (
                <li key={`${issue.code}-${issue.nodeId ?? issue.edgeId ?? ''}-${i}`}>
                  <button
                    type="button"
                    disabled={!issue.nodeId}
                    className="text-body-sm text-text-primary hover:bg-bg-muted focus-visible:shadow-focus-ring flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left outline-none disabled:hover:bg-transparent"
                    onClick={() =>
                      issue.nodeId && store.getState().select([issue.nodeId])
                    }
                  >
                    <Warning
                      aria-hidden
                      className={cn(
                        'mt-0.5 size-3.5 shrink-0',
                        issue.severity === 'error'
                          ? 'text-status-danger-fg'
                          : 'text-status-warning-fg',
                      )}
                    />
                    {issue.message}
                  </button>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}

      <div
        className="bg-bg-subtle flex items-center gap-1.5 rounded-md px-2.5 py-1.5"
        title="Estimated before running; filters can make it lower"
      >
        <Lightning aria-hidden className="text-icon-secondary size-3.5" />
        <span className="text-label-sm text-text-primary">
          {estimate.renders === 0
            ? 'No renders'
            : `${estimate.approximate ? '~' : ''}${estimate.renders} ${estimate.renders === 1 ? 'render' : 'renders'} · ${estimate.credits} credits`}
        </span>
      </div>

      <Menu>
        <MenuTrigger asChild>
          <IconButton icon={DotsThree} aria-label="Flow actions" />
        </MenuTrigger>
        <MenuContent align="end">
          {flow.status === 'ACTIVE' ? (
            <>
              <MenuItem onSelect={() => void setStatus('ACTIVE')}>
                Update active version
              </MenuItem>
              <MenuItem onSelect={() => void setStatus('PAUSED')}>Pause flow</MenuItem>
            </>
          ) : (
            <MenuItem
              disabled={errors.length > 0}
              onSelect={() => void setStatus('ACTIVE')}
            >
              Activate flow
            </MenuItem>
          )}
          {runOpen && <MenuItem onSelect={() => void run.cancel()}>Stop run</MenuItem>}
          <MenuItem onSelect={() => setRenaming(true)}>Rename</MenuItem>
          <MenuDivider />
          <MenuItem type="danger" onSelect={() => setDeleting(true)}>
            Delete flow
          </MenuItem>
        </MenuContent>
      </Menu>
      <Button
        variant="outline"
        leadingIcon={Play}
        disabled={runOpen || run.starting !== null}
        loading={run.starting === 'TEST'}
        onClick={() => startRun('TEST')}
      >
        Test run
      </Button>
      <Button
        leadingIcon={Lightning}
        disabled={runOpen || run.starting !== null}
        loading={run.starting === 'FULL'}
        onClick={() => startRun('FULL')}
      >
        Run flow
      </Button>

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        tone="neutral"
        icon={Warning}
        title={`Run with ${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}?`}
        description={warnings.map((issue, i) => {
          const label = nodeLabel(issue.nodeId);
          return (
            <span key={`${issue.code}-${i}`} className="block not-first:mt-2">
              {label && <strong className="text-text-primary">{label}: </strong>}
              {issue.message}
            </span>
          );
        })}
        cancelLabel="Cancel"
        confirmLabel={confirming === 'TEST' ? 'Test run anyway' : 'Run anyway'}
        onConfirm={() => {
          if (confirming) void run.start(confirming);
          setConfirming(null);
        }}
      />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={`Delete ${flow.name}?`}
        description="The flow and its saved versions are removed. This can’t be undone."
        confirmLabel="Delete flow"
        onConfirm={() => void deleteFlow()}
      />
    </header>
  );
}

function SaveLabel({ status, savedAt }: { status: SaveStatus; savedAt: Date }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  switch (status) {
    case 'saving':
      return <>Saving…</>;
    case 'unsaved':
      return <>Unsaved changes</>;
    case 'conflict':
      return (
        <span className="text-status-danger-fg">
          Changed elsewhere - reload to keep editing
        </span>
      );
    case 'failed':
      return <span className="text-status-warning-fg">Couldn’t save - retrying</span>;
    case 'saved': {
      const minutes = Math.floor((now - savedAt.getTime()) / 60_000);
      if (minutes < 1) return <>Saved just now</>;
      if (minutes < 60) return <>Saved {minutes} min ago</>;
      return (
        <>
          Saved at{' '}
          {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </>
      );
    }
  }
}
